import type { SearchDataQuery, WeightItemFragment } from '#graphql-operations'
import type { GraphqlResponse } from '#nuxt-graphql-middleware/response'
import type { LocationFilter } from '~~/layers/legacy-search/app/types'
import { notNullish } from '@vueuse/core'

type UseSearchData = {
  locations: ComputedRef<LocationFilter[]>
  getTermData: (id: string) => WeightItemFragment | undefined
  getFacetLabel: (id: string) => string | undefined
}

type SearchData = {
  terms: Record<string, WeightItemFragment>
  locations: LocationFilter[]
  facetLabels: Record<string, string>
}

function transformSearchData(
  data: GraphqlResponse<SearchDataQuery>,
): SearchData {
  const terms = data.data.terms.reduce<Record<string, WeightItemFragment>>(
    (acc, item) => {
      if (item?.id) {
        acc[item.id] = item
      }
      return acc
    },
    {},
  )

  const facetLabels = data.data.facetLabels || {}

  const locations = (data.data.locationFilters.items || [])
    .map((v) => {
      if (
        v &&
        'address' in v &&
        v.address?.location?.lat &&
        v.address.location.lon &&
        v.title &&
        v.id
      ) {
        return {
          title: v.title,
          id: v.id,
          lat: v.address.location.lat,
          lon: v.address.location.lon,
          radius: v.radius || 0,
        }
      }
    })
    .filter(notNullish)

  return {
    terms,
    locations,
    facetLabels,
  }
}

export async function useSearchData(): Promise<UseSearchData> {
  const { activeDomain } = useSiteContext()
  const language = useCurrentLanguage()

  const { data } = await useCachedAsyncData(
    'searchData-' + language.value,
    () =>
      useGraphqlQuery(
        'searchData',
        {
          domain: activeDomain,
        },
        {
          graphqlCaching: {
            client: true,
          },
        },
      ),
    {
      transform: transformSearchData,
      dedupe: 'defer',
      deep: false,
      serverMaxAge: 60 * 60 * 24,
      clientMaxAge: 'never',
      serverCacheTags: (data) => data.extensions?.cacheability?.tagsNuxt || [],
      watch: [language],
    },
  )

  function getTermData(id: string) {
    if (!data.value) {
      return
    }
    return data.value.terms[id]
  }

  const locations = computed(() => data.value?.locations || [])

  function getFacetLabel(id: string) {
    return data.value?.facetLabels[id]
  }

  return {
    getTermData,
    locations,
    getFacetLabel,
  }
}
